import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  CANONICAL_BASE,
  CANONICAL_PARAMS_WHITELIST,
  DEFAULT_DESC,
  NOINDEX_ROUTES,
  DEFAULT_KEYWORDS,
  DEFAULT_LOCALE,
  DEFAULT_OG_TYPE,
  DEFAULT_SITE_NAME,
  DEFAULT_TITLE,
} from "../../utils/seoDefaults";

const resolveCanonicalBase = () => CANONICAL_BASE;
const DEFAULT_INDEX_ROBOTS = "index,follow";
const DEFAULT_INDEX_GOOGLEBOT =
  "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1";

const normalizeCanonical = (value, base) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const path = value.startsWith("/") ? value : `/${value}`;
  return `${base}${path}`;
};

const ensureTrailingSlash = (value, base) => {
  if (!value) return "";
  try {
    const url = new URL(value, base);
    const path = url.pathname || "/";
    if (path === "/" || path.endsWith("/")) return url.toString();
    if (/\.[a-z0-9]+$/i.test(path)) return url.toString();
    url.pathname = `${path}/`;
    return url.toString();
  } catch (_) {
    if (value === "/" || value.endsWith("/")) return value;
    return `${value}/`;
  }
};

const buildCanonicalUrl = (location, allowList = CANONICAL_PARAMS_WHITELIST) => {
  const base = resolveCanonicalBase();
  if (!location) return `${base}/`;
  const params = new URLSearchParams(location.search || "");
  const filtered = new URLSearchParams();
  allowList.forEach((key) => {
    const value = params.get(key);
    if (value) filtered.set(key, value);
  });
  const query = filtered.toString();
  const cleanPath = location.pathname || "/";
  return `${base}${cleanPath}${query ? `?${query}` : ""}`;
};

export default function Seo({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESC,
  keywords = DEFAULT_KEYWORDS,
  canonical,
  canonicalParams = CANONICAL_PARAMS_WHITELIST,
  ogType = DEFAULT_OG_TYPE,
  siteName = DEFAULT_SITE_NAME,
  locale = DEFAULT_LOCALE,
  robots,
  googlebot,
  noindex,
  image = "",
  imageAlt = "",
  twitterCard,
}) {
  const location = useLocation();
  const base = resolveCanonicalBase();
  const shouldNoIndex = Boolean(noindex)
    || NOINDEX_ROUTES.some((path) => (location.pathname || "").startsWith(path));
  const resolvedCanonical = ensureTrailingSlash(
    normalizeCanonical(canonical, base) ||
      buildCanonicalUrl(location, canonicalParams),
    base
  );

  const resolvedRobots = shouldNoIndex
    ? "noindex,nofollow"
    : robots || DEFAULT_INDEX_ROBOTS;
  const resolvedGooglebot = shouldNoIndex
    ? "noindex,nofollow"
    : googlebot || DEFAULT_INDEX_GOOGLEBOT;
  const resolvedImage = normalizeCanonical(image, base);
  const resolvedTwitterCard =
    twitterCard || (resolvedImage ? "summary_large_image" : "summary");

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={resolvedCanonical} />

      {(resolvedRobots || resolvedRobots === "") && (
        <meta name="robots" content={resolvedRobots} />
      )}
      {(resolvedGooglebot || resolvedGooglebot === "") && (
        <meta name="googlebot" content={resolvedGooglebot} />
      )}

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={resolvedCanonical} />
      {resolvedImage && <meta property="og:image" content={resolvedImage} />}
      {resolvedImage && <meta property="og:image:secure_url" content={resolvedImage} />}
      {resolvedImage && imageAlt && <meta property="og:image:alt" content={imageAlt} />}

      <meta name="twitter:card" content={resolvedTwitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:url" content={resolvedCanonical} />
      {resolvedImage && <meta name="twitter:image" content={resolvedImage} />}
      {resolvedImage && imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}
    </Helmet>
  );
}
